import { graphql } from 'react-apollo';

import type { DiscoveryCollections_PassThroughParameter as PassThroughParameter } from '__generated__/graphql-types';
import { compose, withProps } from 'recompose';

import DiscoveryCollectionsQuery from 'bundles/browse/components/DiscoveryCollectionsQuery.graphql';
import type {
  DiscoveryCollectionsQuery as DiscoveryCollectionsQueryData,
  DiscoveryCollectionsQueryVariables,
} from 'bundles/browse/components/__generated__/DiscoveryCollectionsQuery';
import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import { NUM_DISCOVERY_COLLECTIONS_ON_SERVER } from 'bundles/browse/constants';
import withCheckElementInViewport from 'bundles/page/components/shared/withCheckElementInViewport';
import inServerContext from 'bundles/ssr/util/inServerContext';

const LAST_COLLECTIONS_SECTION_SELECTOR = '.rc-CollectionsSection:last-of-type';
// Only update this on client, otherwise value could be used between requests.
let hasRenderedAllCollections = false;

type PropsFromCaller = {
  contextType: string;
  contextId: string;
  passThroughParameters?: PassThroughParameter[];
};

type PropsToComponent = {
  discoveryCollections?: Array<DiscoveryCollection | null>;
  isDiscoveryCollectionsLoading?: boolean;
};

const withDiscoveryCollections = () => {
  return compose(
    graphql<PropsFromCaller, DiscoveryCollectionsQueryData, DiscoveryCollectionsQueryVariables, PropsToComponent>(
      DiscoveryCollectionsQuery,
      {
        options: ({ contextId, contextType, passThroughParameters }) => {
          return {
            variables: {
              contextId,
              contextType,
              passThroughParameters,
            },
            context: { clientName: 'gatewayGql' },
            errorPolicy: 'all', //Ensures partial data is returned even if product-card has errors
          };
        },
        props: ({ data }) => {
          return {
            allDiscoveryCollections: data?.DiscoveryCollections?.queryCollections
              ? data.DiscoveryCollections.queryCollections
              : undefined,
            isDiscoveryCollectionsLoading: data?.loading,
          };
        },
      }
    ),
    /* 
      The following handles dynamically loading collections based on different contexts.
      On server we only want to load a subset of collections, even though we fetch all collections on server, rendering less in SSR helps with TBT.
      On client, once the user scrolls to the beginning of the last collection section we render the rest.
    */
    withCheckElementInViewport(LAST_COLLECTIONS_SECTION_SELECTOR, undefined, 0.1),
    withProps(
      ({
        isElementInViewport,
        elementNotFound,
        allDiscoveryCollections,
      }: {
        isElementInViewport: boolean;
        elementNotFound: boolean;
        allDiscoveryCollections: Array<DiscoveryCollection | null>;
      }) => {
        // If on client and user has already rendered all collections or the last collection is in the viewport, render all collections.
        if (!inServerContext && (hasRenderedAllCollections || isElementInViewport || elementNotFound)) {
          hasRenderedAllCollections = true;
          return {
            discoveryCollections: allDiscoveryCollections,
          };
          // If on server or user has not reached the last collection.
        } else {
          return {
            discoveryCollections: allDiscoveryCollections?.slice(0, NUM_DISCOVERY_COLLECTIONS_ON_SERVER),
          };
        }
      }
    )
  );
};

export default withDiscoveryCollections;
